import type { EditSelectOptionList } from 'components/forms/EditSelect';

import { MAX_FISCAL_PERIOD_DAYS } from 'utils/fiscalPeriods';

import { dayOfWeekName, monthName, ordinalSuffix, ordinalWord, periodName, weekOfMonthName } from './scheduleLabels';

// Option lists for an opening rule's calendar fields. Plain constants rather than
// bespoke components: each is an `EditSelect` over a fixed list, and a component per
// list would add a file and a prop signature without adding behaviour.
//
// Every list starts with an empty option holding `null`, which is what "not narrowed
// by this field" means. Its key is '' so the browser's own `required` validation
// still treats it as no selection.

const NONE = { key: '', value: null, label: '—' } as const;

const range = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, i) => from + i);

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const MONTH_OPTIONS: EditSelectOptionList<number | null> = [NONE, ...range(1, 12).map((m) => ({ key: m, value: m, label: monthName(m) }))];

/** Same wire values as months; labels say Period N so fiscal rules are not read as calendar months. */
export const PERIOD_OPTIONS: EditSelectOptionList<number | null> = [NONE, ...range(1, 12).map((p) => ({ key: p, value: p, label: periodName(p) }))];

// 1–5, where 5 means "last" — the backend's own range for week_of_month.
export const WEEK_OF_MONTH_OPTIONS: EditSelectOptionList<number | null> = [
  NONE,
  ...range(1, 5).map((w) => ({ key: w, value: w, label: capitalize(weekOfMonthName(w)) })),
];

/** Same values and ordinal labels as week-of-month; field chrome says "period" instead. */
export const WEEK_OF_PERIOD_OPTIONS = WEEK_OF_MONTH_OPTIONS;

// ISO-8601 numbering: 1 = Monday … 7 = Sunday, matching the backend's 1–7 range.
export const DAY_OF_WEEK_OPTIONS: EditSelectOptionList<number | null> = [NONE, ...range(1, 7).map((d) => ({ key: d, value: d, label: dayOfWeekName(d) }))];

/**
 * Nth-of-month (or nth-of-period), counted from either end.
 *
 * Sign convention rather than a separate direction field: **positive counts from
 * the start** (1 = the 1st), **0 means the last day**, and **negative counts back
 * from the end** (−1 = the day before last). The two group headings carry that,
 * since the numbers alone do not say which end they count from.
 *
 * Calendar months accept −30…31. Fiscal periods take `maxFromStart` from the
 * active fiscal pattern via {@link maxPeriodDaysForPattern} (35 for 4-5-4 / 5-4-4,
 * 42 for 4-4-5 with a 53rd week on period 12).
 */
export const nthOptions = (unit: 'month' | 'period', maxFromStart: number): EditSelectOptionList<number | null> => {
  const maxFromEnd = maxFromStart - 1;
  return [
    NONE,
    {
      key: 'from-start',
      label: `Counting from the start of the ${unit}`,
      options: range(1, maxFromStart).map((n) => ({ key: n, value: n, label: ordinalWord(n) })),
    },
    {
      key: 'from-end',
      label: `Counting back from the end of the ${unit}`,
      options: [
        { key: 0, value: 0, label: 'Last day' },
        ...range(1, maxFromEnd).map((n) => ({ key: -n, value: -n, label: `${n}${ordinalSuffix(n)} from last` })),
      ],
    },
  ];
};

export const NTH_OF_MONTH_OPTIONS: EditSelectOptionList<number | null> = nthOptions('month', 31);

/** Flat fallback covering every shipped pattern when fiscal config is unavailable. */
export const NTH_OF_PERIOD_OPTIONS: EditSelectOptionList<number | null> = nthOptions('period', MAX_FISCAL_PERIOD_DAYS);

/** Pattern-aware nth-of-period list (prefer over {@link NTH_OF_PERIOD_OPTIONS} when config is known). */
export const nthOfPeriodOptionsForMaxDays = (maxDays: number): EditSelectOptionList<number | null> => nthOptions('period', maxDays);
